import React from "react";
import moment from "moment";
import { maskCNPJ } from "../../../utils/masks";
import "./CreditoPreditivo.css";

/* ================== Formatação ================== */
function numberBR(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}
function moneyBR(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}
function percentBR(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const x = Number(n) * 100;
  return `${x.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
}
function companyAge(isoOrBr) {
  if (!isoOrBr) return null;
  const m = isoOrBr.includes("/")
    ? moment(isoOrBr, "DD/MM/YYYY")
    : moment(isoOrBr);
  if (!m.isValid()) return null;
  const years = moment().diff(m, "years");
  return `${years} ano${years === 1 ? "" : "s"}`;
}

/* Mapa de cor para os sinais-chave → classe do tile */
function signalTone(value, type) {
  const v = (value || "").toUpperCase();
  const g = "green";
  const a = "amber";
  const r = "red";
  const maps = {
    gravidadeAtual: {
      BAIXA: g,
      MÉDIA: a,
      MEDIA: a,
      ALTA: r,
      CRÍTICA: r,
      CRITICA: r,
    },
    recenciaOcorrencias: {
      ANTIGA: g,
      INTERMEDIÁRIA: a,
      INTERMEDIARIA: a,
      RECENTE: r,
    },
    concentracaoRisco: { BAIXA: g, MÉDIA: a, MEDIA: a, ALTA: r },
    consistenciaOperacional: { ALTA: g, MÉDIA: a, MEDIA: a, BAIXA: r },
  };
  return (maps[type] && maps[type][v]) || "neutral";
}

/* ================== Gauge (SVG simples) ================== */
function Gauge({ score = 0 }) {
  const size = 200;
  const stroke = 16;
  const r = (size - stroke) / 2;
  const cx = size / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, (Number(score) / 1000) * 100));
  const offset = circ - (pct / 100) * circ;

  return (
    <div className="cp__gauge">
      <svg viewBox={`0 0 ${size} ${size}`}>
        <circle className="cp__gauge-track" cx={cx} cy={cx} r={r} />
        <circle
          className="cp__gauge-arc"
          cx={cx}
          cy={cx}
          r={r}
          strokeDasharray={circ}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="cp__gauge-center">
        <div className="cp__gauge-value">{Math.round(Number(score) || 0)}</div>
        <div className="cp__gauge-max">de 1000</div>
      </div>
    </div>
  );
}

function CardHead({ icon, title, sub }) {
  return (
    <div className="cp__card-head">
      <div className="cp__card-icon">{icon}</div>
      <div>
        <div className="cp__card-title">{title}</div>
        {sub && <div className="cp__card-sub">{sub}</div>}
      </div>
    </div>
  );
}

const TONE_LEVEL = { green: 1, amber: 2, red: 3, neutral: 0 };

function Signal({ label, value, type }) {
  const tone = signalTone(value, type);
  const level = TONE_LEVEL[tone] || 0;
  return (
    <div className="cp__signal">
      <div className="cp__signal-label">{label}</div>
      <div
        className="cp__meter"
        role="meter"
        aria-valuenow={level}
        aria-valuemax={3}
      >
        {[1, 2, 3].map((seg) => (
          <span
            key={seg}
            className={`cp__meter-seg${
              seg <= level ? ` cp__meter-seg--${tone}` : ""
            }`}
          />
        ))}
      </div>
      <div className={`cp__signal-value cp__signal-value--${tone}`}>
        {value || "—"}
      </div>
    </div>
  );
}

/* ================== Componente principal ================== */
export default function CreditoPreditivo({ data }) {
  const d = data || {};
  const dc = d.dadosCadastrais || {};
  const m = d.metricasDerivadas || {};
  const rec = d.recomendacaoDeCredito || {};
  const pano = d.panoramaTemporal || {};

  const risk = d.classificacaoRisco;
  const idade = companyAge(dc.dataFundacao);
  const eventos = pano.eventosPorMes || [];
  const maxValor = Math.max(
    1,
    ...eventos.map((e) => Number(e.valorTotal || 0)),
  );
  const drivers = d.driversDeRisco || [];
  const maxAbsImpacto = Math.max(
    1,
    ...drivers.map((x) => Math.abs(Number(x.impacto || 0))),
  );

  return (
    <div className="cp" data-risk={risk || undefined}>
      <section className="cp__card">
        <CardHead icon="📌" title="Resumo" sub={pano.janelaAnalisada} />
        <p className="cp__resumo">
          {d.resumoExecutivo || pano.observacao || (
            <span className="cp__empty">Sem resumo disponível.</span>
          )}
        </p>
      </section>
      {/* ── SCORE + RESUMO ── */}
      <div className="cp__row cp__row--score">
        <section className="cp__card cp__score">
          <CardHead icon="🎯" title="Score estimado" />
          <Gauge score={Number(d.scoreIA ?? 0)} />
          <div className="cp__prob">
            <div className="cp__prob-value">
              {numberBR(d.probabilidadeInadimplencia12M)}%
            </div>
            <div className="cp__prob-label">
              Prob. de inadimplência em 12 meses
            </div>
          </div>
        </section>

        <div className="cp__col">
          <section className="cp__card">
            <CardHead icon="📝" title="Recomendação de crédito" />
            <div className="cp__rec-key">
              <div className="cp__rec-decision">
                <div className="cp__rec-decision-label">Decisão</div>
                <div className="cp__rec-decision-value">
                  {rec.decisao || "—"}
                </div>
              </div>
              <div className="cp__rec-limit">
                <div className="cp__rec-limit-label">Limite sugerido</div>
                <div className="cp__rec-limit-value">
                  {moneyBR(rec.limiteSugerido?.valor)}
                </div>
              </div>
            </div>
            {(rec.condicoes || []).length > 0 && (
              <div className="cp__list">
                {rec.condicoes.map((c, i) => (
                  <div className="cp__list-item" key={i}>
                    {c}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>

      {/* ── SINAIS + DRIVERS ── */}
      <div className="cp__row cp__row--2">
        <section className="cp__card">
          <CardHead icon="🧭" title="Sinais-chave" />
          <div className="cp__signals">
            <Signal
              label="Gravidade atual"
              value={d.sinaisChave?.gravidadeAtual}
              type="gravidadeAtual"
            />
            <Signal
              label="Recência"
              value={d.sinaisChave?.recenciaOcorrencias}
              type="recenciaOcorrencias"
            />
            <Signal
              label="Concentração"
              value={d.sinaisChave?.concentracaoRisco}
              type="concentracaoRisco"
            />
            <Signal
              label="Consistência"
              value={d.sinaisChave?.consistenciaOperacional}
              type="consistenciaOperacional"
            />
          </div>
        </section>

        <section className="cp__card">
          <CardHead icon="⚠️" title="Fatores que impactam o score" />
          <div className="cp__drivers">
            {drivers.length === 0 && (
              <span className="cp__empty">Nenhum fator informado.</span>
            )}
            {drivers.map((drv, i) => {
              const impacto = Number(drv.impacto || 0);
              const neg = impacto < 0;
              const w = Math.round((Math.abs(impacto) / maxAbsImpacto) * 100);
              return (
                <div className="cp__driver" key={i}>
                  <div className="cp__driver-head">
                    <span className="cp__driver-name">{drv.nome}</span>
                    <span
                      className={`cp__driver-impact cp__driver-impact--${
                        neg ? "neg" : "pos"
                      }`}
                    >
                      {impacto > 0 ? "+" : ""}
                      {impacto}
                    </span>
                  </div>
                  {drv.descricao && (
                    <div className="cp__driver-desc">{drv.descricao}</div>
                  )}
                  <div className="cp__driver-bar">
                    <div
                      className={`cp__driver-fill cp__driver-fill--${
                        neg ? "neg" : "pos"
                      }`}
                      style={{ width: `${w}%` }}
                    />
                  </div>
                  {drv.peso != null && (
                    <div className="cp__driver-weight">
                      Peso {Math.round((drv.peso || 0) * 100)}%
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {/* ── PANORAMA TEMPORAL ── */}
      {eventos.length > 0 && (
        <section className="cp__card">
          <CardHead
            icon="📈"
            title="Panorama temporal"
            sub={pano.janelaAnalisada}
          />
          {pano.observacao && <p className="cp__resumo">{pano.observacao}</p>}
          <div className="cp__chart">
            <div className="cp__chart-plot">
              {eventos.map((e) => {
                const valor = Number(e.valorTotal || 0);
                const pct = Math.round((valor / maxValor) * 100);
                const ocorrencias =
                  Number(e.protestos || 0) +
                  Number(e.pendencias || 0) +
                  Number(e.chequesSemFundo || 0);
                return (
                  <div
                    className="cp__bar-col"
                    key={e.mes}
                    title={`${moment(e.mes, "YYYY-MM").format(
                      "MM/YYYY",
                    )} · ${moneyBR(valor)}\nProtestos: ${e.protestos ?? 0} · Pendências: ${
                      e.pendencias ?? 0
                    } · Cheques: ${e.chequesSemFundo ?? 0}`}
                  >
                    <div className="cp__bar-value">{moneyBR(valor)}</div>
                    <div className="cp__bar-wrap">
                      <div
                        className="cp__bar"
                        style={{ height: `${Math.max(pct, 2)}%` }}
                      >
                        {ocorrencias > 0 && (
                          <span className="cp__bar-count">{ocorrencias}</span>
                        )}
                      </div>
                    </div>
                    <div className="cp__bar-x">
                      {moment(e.mes, "YYYY-MM").format("MMM/YY")}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="cp__chart-legend">
              <span className="cp__chart-legend-item">
                <i className="cp__chart-swatch" /> Valor total no mês
              </span>
              <span className="cp__chart-legend-item">
                <b>N</b> ocorrências (protestos + pendências + cheques)
              </span>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
